import { assert } from '../app/logger.js';
import { Dialog } from '../controls/dialog.js';
import { listenToEvent } from '../util/listen_to_event.js';

import '../controls/range_control.js';
import '../controls/rating_control.js';
import '../media/picupload_list.js';

/**
 * Review editable form.
 */
class ReviewDialog extends Dialog {
  static titleMarkup = `
<span class='designation'></span> Review<div class='divider'></div>
  `;

  static bodyMarkup = `
<label class='group-header'>
  Date of visit
  <input type='date' class='editable-control date'>
</label>
<label class='date-is-approximate-label'>
  <span>Date is approximate</span>
  <input type='checkbox' class='date-is-approximate'>
</label>
<div class='divider'></div>
<div class='group ratings-after'>
  <div class='group-header'>Overall rating</div>
  <rating-control rating-id='overall'
                  class='rating-control'></rating-control>
</div>
<div class='divider ratings-before'></div>
<label class='group-header'>
  Add a headline
  <input class='editable-control headline'>
</label>
<label class='group-header'>
  Write a review
  <textarea class='editable-control text'
            rows='6'></textarea>
</label>
<div class='group-header'>
  Add photos
  <picupload-list></picupload-list>
</div>
  `;

  static buttons = {
    accept: {
      label: 'Submit',
      default: true,
    },
    cancel: {
      label: 'Cancel',
    },
  };

  set({
    loc,
    id = 0,
    headline = '',
    text = '',
    date = '',
    dateIsApproximate = false,
    pics = [],
    ratings = [
      {
        id: 'overall',
        value: 0,
      },
    ],
  }) {
    this.#id = id;
    this.loc = loc;

    this.acceptButton.textContent = id ? 'Change' : 'Add';
    this.designationElement.innerHTML = loc.name;
    this.setRatingsMarkup(loc.typeInfo.ratings ?? []);

    this.dateElement.value = date;
    this.dateIsApproximateElement.checked = dateIsApproximate;
    this.headlineElement.value = headline;
    this.textElement.value = text;
    this.picListElement.pics = pics;
    for (let { id, value } of ratings) {
      let ratingEl = this.getRatingElement(id);
      assert(ratingEl, `No rating element for ${id}`);
      this.setRatingElementValue(ratingEl, value);
    }
  }

  setRatingsMarkup(ratings) {
    while (
      this.ratingsBeforeElement.previousElementSibling !=
      this.ratingsAfterElement
    ) {
      this.ratingsBeforeElement.previousElementSibling.remove();
    }

    let ratingsHTML = '';
    let ratingDeps = [];
    for (let group of ratings) {
      assert(group.type == 'group', `[review] wrong rating group type`);
      ratingsHTML += `
<div class='divider'></div>
<div class='${group.id} group'>
  <div class='group-header'>${group.dialogLabel || group.label}</div>
  <div class='group-body'>`;

      for (let rating of group.items) {
        assert(
          rating,
          `[review] mistyped rating at ${group.items.indexOf(rating)}`
        );
        switch (rating.type) {
          case 'boolean':
            ratingsHTML += `
              <label class='boolean'>
                <input type='checkbox'
                       rating-id='${rating.id}'
                       class='rating-control'
                       >${rating.label}
              </label>`;
            break;

          case 'range':
            {
              let min = rating.min.defaultValue ?? rating.min;
              if (rating.min.event) {
                ratingDeps.push(
                  Object.assign(
                    {
                      targetId: rating.id,
                      targetProperty: 'min',
                    },
                    rating.min
                  )
                );
              }
              ratingsHTML += `
              <range-control rating-id='${rating.id}'
                             class='rating-control'
                             label='${rating.label}'
                             min='${min}'
                             max='${rating.max}'></range-control>`;
            }
            break;

          case 'rating':
            {
              let classes = rating.imageClass
                ? `origin ${rating.imageClass}`
                : '';
              ratingsHTML += `
              <rating-control rating-id='${rating.id}'
                              class='rating-control ${classes}'
                              label='${rating.label}'
                              ${
                                (rating.scale &&
                                  `scale-labels='${rating.scale}'`) ||
                                ''
                              }></rating-control>`;
            }
            break;

          case 'text':
            ratingsHTML += `
              <label class='text'>${rating.label}
                <input type='text'
                       rating-id='${rating.id}'
                       class='rating-control'>
              </label>`;
            break;

          default:
            break;
        }
      }
      ratingsHTML += `</div></div>`;
    }

    let template = document.createElement('template');
    template.innerHTML = ratingsHTML;
    this.ratingsAfterElement.after(template.content.cloneNode(true));

    // Rating dependencies.
    for (let { id, event, property, targetId, targetProperty } of ratingDeps) {
      let el = this.ratingElementBy(id);
      listenToEvent(el, event, () => {
        let targetEl = this.ratingElementBy(targetId);
        switch (targetProperty) {
          case 'min':
            {
              let value = el[property];
              if (targetEl.unrated || targetEl.value < value) {
                targetEl.value = value;
              }
              targetEl.min = value;
            }
            break;
        }
      });
    }
  }

  get data() {
    return {
      id: this.#id,
      locId: this.loc.id,
      date: this.dateElement.value || null,
      dateIsApproximate: this.dateIsApproximateElement.checked,
      headline: this.headlineElement.value,
      text: this.textElement.value,
      pics: this.picListElement.pics,
      ratings: Array.from(this.allRatingElements)
        .map(el => [
          el.getAttribute('rating-id'),
          this.getRatingElementValue(el),
        ])
        .filter(([ratingId, ratingValue]) => {
          let ratingInfo = this.loc.ratingInfoById(ratingId);
          switch (ratingInfo && ratingInfo.type) {
            case 'boolean':
            case 'rating':
            case 'text':
              return !!ratingValue;
          }
          return true;
        }),
    };
  }

  getRatingElementValue(el) {
    if (el.localName == 'input' && el.type == 'checkbox') return el.checked;
    return el.value;
  }

  setRatingElementValue(el, value) {
    if (el.localName == 'input' && el.type == 'checkbox') {
      el.checked = value;
    } else {
      el.value = value;
    }
  }

  getRatingElement(ratingId) {
    return this.bodyElement.querySelector(
      this.constructor.selectors.RatingBy(ratingId)
    );
  }

  static get selectors() {
    const baseSelectors = super.selectors;
    const { Body, Title } = baseSelectors;
    return Object.assign({}, baseSelectors, {
      AllRatings: `${Body} .rating-control`,
      Date: `${Body} .date`,
      DateIsApproximate: `${Body} .date-is-approximate`,
      Designation: `${Title} .designation`,
      Header: `${Body} .header`,
      Headline: `${Body} .headline`,
      OverallRating: `${Body} [rating-id=overall]`,
      RatingBy(ratingId) {
        return `[rating-id='${ratingId}']`;
      },
      RatingsAfter: `${Body} .ratings-after`,
      RatingsBefore: `${Body} .ratings-before`,
      Text: `${Body} textarea.text`,
      PicList: `${Body} picupload-list`,
    });
  }

  static get elements() {
    let s = this.selectors;
    return {
      allRatingElements: s.AllRatings,
      dateElement: s.Date,
      dateIsApproximateElement: s.DateIsApproximate,
      designationElement: s.Designation,
      headlineElement: s.Headline,
      picListElement: s.PicList,
      ratingsAfterElement: s.RatingsAfter,
      ratingsBeforeElement: s.RatingsBefore,
      textElement: s.Text,
      ratingElementBy: ratingId => s.RatingBy(ratingId),
    };
  }

  static tagName = 'review-dialog';

  #id;
}

ReviewDialog.defineCustomElement();

export { ReviewDialog };
